import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  CircleDot,
  Clock,
  CornerDownRight,
  Layers,
  Quote,
  Trophy,
  Users,
} from "lucide-react";
import {
  buildP2PShareLink,
  buildP2PShareText,
  calculateClosureProgress,
  calculateTimeRemaining,
  canCancelP2P,
  canEditP2P,
  formatOutcome,
  formatCounterSideLabel,
  formatP2PRosterTime,
  getCardAction,
  getCreatorBudget,
  getOfferedOdds,
  getRemainingCounterCapacity,
  getJoinedCount,
  getLiveScoreLine,
  getOwner,
  getPendingInvites,
  getP2PRosterEntries,
  getP2PWinPayout,
  getUserStake,
  getMinCounterStake,
  parseOutcomeParts,
  P2P_TERMS,
  sameUserId,
  statusTone,
} from "../../../utils/p2pHelpers";
import ShareIconButton from "../../ShareIconButton";
import "../../../share-icon.css";
import {
  acceptP2PInviteWithAck,
  cancelP2PBetWithAck,
  editP2PBetWithAck,
  joinP2PBetWithAck,
  rejectP2PInviteWithAck,
} from "../../../redux/p2pSocket";
import JoinP2PModal from "../JoinP2PModal";
import P2PHowItWorks from "../P2PHowItWorks";
import CancelP2PModal from "../CancelP2PModal";
import EditP2PModal from "../EditP2PModal";
import { formatAppDateTime } from "../../../utils/appTimeFormat";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { usePersistedPanelSize } from "../../../hooks/usePersistedPanelSize";
import { useHorizontalPanelResize } from "../../../hooks/useHorizontalPanelResize";

const STANCE_RAIL_WIDE = "(min-width: 1201px)";
const STANCE_RAIL_DEFAULT = 320;
const STANCE_RAIL_MIN = 240;
const STANCE_RAIL_MAX = 440;

const initials = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

const relativeTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const delta = Math.max(0, (Date.now() - t) / 1000);
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
};

const FormStrip = ({ form, label }) => {
  if (!form) {
    return (
      <div className="stance-form stance-form-empty" aria-label={`${label}: no form`}>
        <span>— — — — —</span>
      </div>
    );
  }
  const chars = form.split("").slice(-5);
  return (
    <div className="stance-form" aria-label={`${label} form: ${form.slice(-5)}`}>
      {chars.map((c, i) => (
        <span key={i} className={`stance-form-dot stance-form-${c.toLowerCase()}`}>
          {c.toUpperCase()}
        </span>
      ))}
    </div>
  );
};

const MoveLead = ({ children, muted }) => (
  <p className={`stance-move-lead${muted ? " stance-move-lead-muted" : ""}`}>
    {children}
  </p>
);

const MovePickChip = ({ tag, label }) => (
  <div className="stance-move-pick">
    <span className="stance-move-pick-tag">{tag}</span>
    <strong>{label}</strong>
  </div>
);

const MoveStats = ({ items }) => (
  <dl className="stance-move-stats">
    {items.map((item) => (
      <div
        key={item.label}
        className={item.highlight ? "stance-move-stat-win" : undefined}
      >
        <dt>{item.label}</dt>
        <dd>
          {item.value}
          {item.unit ? <small>{item.unit}</small> : null}
        </dd>
      </div>
    ))}
  </dl>
);

const moveBadgeLabel = (tone) => {
  if (tone === "joined") return P2P_TERMS.againstTag;
  if (tone === "owner") return "Your stance";
  if (tone === "join") return "Open";
  if (tone === "add-stake" || tone === "add-spots") return "Add stake";
  if (tone === "invited") return "Invited";
  if (tone === "full") return "Full";
  if (tone === "closed") return "Closed";
  return null;
};

const TeamMark = ({ team, side }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`stance-team-mark stance-team-mark-${side}`}>
      {team?.logo && !failed ? (
        <img src={team.logo} alt="" onError={() => setFailed(true)} />
      ) : (
        <span>{(team?.name || "?").slice(0, 1).toUpperCase()}</span>
      )}
    </span>
  );
};

const PitDuelSlip = ({
  bet,
  siblingStances = [],
  onSelectStance,
  userId,
  onNotify,
  onUpdated,
  onRefresh,
}) => {
  const [busy, setBusy] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [joinMode, setJoinMode] = useState("join");
  const [countdown, setCountdown] = useState("");
  const [timeProgress, setTimeProgress] = useState(0);
  const isWideStance = useMediaQuery(STANCE_RAIL_WIDE);
  const {
    size: railWidth,
    setClampedSize: setRailWidth,
    reset: resetRailWidth,
    minSize: railMin,
    maxSize: railMax,
  } = usePersistedPanelSize("bb-pit-stance-rail-w", {
    defaultSize: STANCE_RAIL_DEFAULT,
    minSize: STANCE_RAIL_MIN,
    maxSize: STANCE_RAIL_MAX,
  });
  const onRailResizePointerDown = useHorizontalPanelResize(
    railWidth,
    setRailWidth,
    "end"
  );

  const fixture = bet.fixture || {};
  const home = fixture.teams?.home || {};
  const away = fixture.teams?.away || {};
  const joined = getJoinedCount(bet);
  const remainingCap = getRemainingCounterCapacity(bet);
  const action = getCardAction(bet, userId);
  const tone = statusTone(bet.status);
  const owner = getOwner(bet);
  const stance = formatOutcome(bet.agreedOdds, fixture);
  const parts = useMemo(
    () => parseOutcomeParts(bet.agreedOdds, fixture),
    [bet.agreedOdds, fixture]
  );
  const counterOptions = useMemo(() => {
    if (!parts?.options || !parts?.key) return [];
    return parts.options.filter((o) => o.id !== parts.key);
  }, [parts]);
  const counterLabel = useMemo(
    () => formatCounterSideLabel(counterOptions),
    [counterOptions]
  );
  const rosterEntries = useMemo(() => getP2PRosterEntries(bet), [bet]);

  const ownerCanCancel =
    userId && sameUserId(bet.user, userId) && canCancelP2P(bet);
  const ownerCanEdit =
    userId && sameUserId(bet.user, userId) && canEditP2P(bet);
  const invite = bet.invites?.find((i) => sameUserId(i.user, userId));
  const showReject =
    invite && invite.status !== "Rejected" && invite.status !== "Accepted";
  const preds = fixture.predictionCounts;
  const standings = fixture.standings;
  const live = ["1H", "HT", "2H", "ET", "LIVE"].includes(
    (fixture.status?.short || "").toUpperCase()
  );
  const kickoff = fixture.date ? formatAppDateTime(fixture.date) : "Kickoff TBD";
  const posted = relativeTime(bet.createdAt);
  const liveScore = getLiveScoreLine(fixture);
  const winPayout = getP2PWinPayout(bet);
  const pendingInvites = getPendingInvites(bet);
  const outcomeStatus = bet.p2pStatus;

  useEffect(() => {
    const gameTime = fixture.date || bet.gameTime;
    const tick = () => {
      setCountdown(calculateTimeRemaining(gameTime));
      setTimeProgress(calculateClosureProgress(gameTime, bet.createdAt));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [fixture.date, bet.gameTime, bet.createdAt]);

  const notify = (message, type = "success") =>
    onNotify?.({ message, type });

  const handleJoinConfirm = async ({ stake } = {}) => {
    if (!userId) return;
    const stakeAmount = Math.max(
      getMinCounterStake(bet),
      Number(stake) || getMinCounterStake(bet),
    );
    const userStake = getUserStake(bet, userId);
    const isAddStake = userStake > 0 || joinMode === "add-stake";
    setBusy(true);
    try {
      const payload = {
        userId,
        p2pId: bet._id,
        stake: stakeAmount,
      };
      if (isAddStake) {
        payload.idempotencyKey = `add-${bet._id}-${userId}-${stakeAmount}-${Date.now()}`;
      }
      const useAccept =
        joinMode === "invite" ||
        (isAddStake &&
          bet.invites?.some(
            (i) => sameUserId(i.user, userId) && i.status === "Accepted"
          ) &&
          !bet.peopleJoined?.some((p) => sameUserId(p.user, userId)));
      const response = await (useAccept ? acceptP2PInviteWithAck : joinP2PBetWithAck)(
        payload
      );
      setJoinOpen(false);
      notify(
        response?.message ||
          (isAddStake ? "Stake added." : "Position taken.")
      );
      onUpdated?.(response?.p2pBet);
      onRefresh?.();
    } catch (err) {
      notify(err.message || "Could not join.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const response = await cancelP2PBetWithAck({ userId, p2pId: bet._id });
      setCancelOpen(false);
      notify(response?.message || "Wager cancelled.");
      onRefresh?.();
    } catch (err) {
      notify(err.message || "Could not cancel.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleEditConfirm = async ({ creatorBudget, offeredOdds }) => {
    if (!userId) return;
    setBusy(true);
    try {
      const response = await editP2PBetWithAck({
        userId,
        p2pId: bet._id,
        creatorBudget,
        offeredOdds,
      });
      setEditOpen(false);
      notify(response?.message || "Offer updated.");
      onUpdated?.(response?.p2pBet);
      onRefresh?.();
    } catch (err) {
      notify(err.message || "Could not update.", "error");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleRejectInvite = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await rejectP2PInviteWithAck({ userId, p2pId: bet._id });
      notify("Invite rejected.");
      onRefresh?.();
    } catch (err) {
      notify(err.message || "Could not reject.", "error");
    } finally {
      setBusy(false);
    }
  };

  const openJoin = (mode = "join") => {
    setJoinMode(mode);
    setJoinOpen(true);
  };

  const renderActions = () => {
    if (action.tone === "login") {
      return (
        <Link
          to="/app/login"
          state={{ from: `/app/p2p?w=${encodeURIComponent(bet._id)}` }}
          className="stance-cta"
        >
          {P2P_TERMS.loginToBetAgainst}
        </Link>
      );
    }
    if (ownerCanEdit || ownerCanCancel) {
      return (
        <div className="stance-actions">
          {ownerCanEdit ? (
            <button
              type="button"
              className="stance-cta stance-cta-secondary"
              disabled={busy}
              onClick={() => setEditOpen(true)}
            >
              Edit offer
            </button>
          ) : null}
          {ownerCanCancel ? (
          <button
            type="button"
            className="stance-ghost stance-ghost-danger"
            disabled={busy}
            onClick={() => setCancelOpen(true)}
          >
            Withdraw
          </button>
          ) : null}
        </div>
      );
    }
    if (action.tone === "join" || action.tone === "add-stake" || action.tone === "add-spots") {
      return (
        <button
          type="button"
          className="stance-cta"
          disabled={busy}
          onClick={() =>
            openJoin(
              action.tone === "add-stake" || action.tone === "add-spots"
                ? "add-stake"
                : "join"
            )
          }
        >
          {action.tone === "add-stake" || action.tone === "add-spots"
            ? "Add stake"
            : `Counter · min stake ${(
                getMinCounterStake(bet)
              ).toLocaleString()}`}
        </button>
      );
    }
    if (action.tone === "invited") {
      return (
        <div className="stance-actions">
          {showReject && (
            <button
              type="button"
              className="stance-ghost"
              disabled={busy}
              onClick={handleRejectInvite}
            >
              Decline
            </button>
          )}
          <button
            type="button"
            className="stance-cta"
            disabled={busy}
            onClick={() => openJoin("invite")}
          >
            Accept · min stake{" "}
            {(getMinCounterStake(bet)).toLocaleString()}
          </button>
        </div>
      );
    }
    if (action.tone === "joined") return null;
    return (
      <span className={`stance-cta stance-cta-${action.tone}`}>{action.label}</span>
    );
  };

  const stakeStat = {
    label: "Odds",
    value: `@${getOfferedOdds(bet).toFixed(2)}`,
  };
  const minStakeStat = {
    label: "Min stake",
    value: getMinCounterStake(bet),
    unit: "cash",
  };
  const winStat =
    winPayout > 0
      ? { label: "Win", value: winPayout, unit: "cash", highlight: true }
      : null;

  const renderMoveBody = () => {
    if (action.tone === "login") {
      return (
        <MoveLead>
          Log in to {P2P_TERMS.betAgainst.toLowerCase()}{" "}
          <b>{owner.name || "creator"}</b>.
        </MoveLead>
      );
    }
    if (action.tone === "owner") {
      return (
        <>
          <MovePickChip
            tag={P2P_TERMS.yourBacking}
            label={parts?.pickLabel || stance}
          />
          <MoveStats
            items={[
              {
                label: "Counters",
                value: `${joined} · ${remainingCap.toLocaleString()} open`,
              },
              stakeStat,
              minStakeStat,
              ...(winStat ? [winStat] : []),
            ]}
          />
          {joined === 0 && (
            <MoveLead muted>{P2P_TERMS.waitingForAgainst}</MoveLead>
          )}
        </>
      );
    }
    if (action.tone === "joined") {
      return (
        <>
          <MovePickChip tag={P2P_TERMS.againstTag} label={counterLabel} />
          <MoveStats
            items={[stakeStat, minStakeStat, ...(winStat ? [winStat] : [])]}
          />
        </>
      );
    }
    if (action.tone === "invited") {
      return (
        <>
          <MoveLead>
            <b>{owner.name || "Creator"}</b> invited you to this duel.
          </MoveLead>
          <MovePickChip tag={P2P_TERMS.againstTag} label={counterLabel} />
          <MoveStats items={[stakeStat, minStakeStat, ...(winStat ? [winStat] : [])]} />
        </>
      );
    }
    if (action.tone === "join" || action.tone === "add-stake" || action.tone === "add-spots") {
      return (
        <>
          <MoveLead>
            {action.tone === "add-stake" || action.tone === "add-spots"
              ? "Add more stake against this backing"
              : (
                <>
                  Against <b>{owner.name || "creator"}</b>&apos;s backing
                </>
              )}
          </MoveLead>
          <MovePickChip tag={P2P_TERMS.againstTag} label={counterLabel} />
          <MoveStats items={[stakeStat, minStakeStat, ...(winStat ? [winStat] : [])]} />
        </>
      );
    }
    if (action.tone === "full") {
      return (
        <MoveLead muted>
          Counter capacity full — this stance is matched.
        </MoveLead>
      );
    }
    return (
      <MoveLead muted>
        {action.label} — you can&apos;t bet against now.
      </MoveLead>
    );
  };

  const moveBadge = moveBadgeLabel(action.tone);

  const renderRoster = () => (
    <section className="stance-panel stance-panel-roster">
      <header>
        <span>{P2P_TERMS.rosterAgainst}</span>
        <em>
          {joined} in · {remainingCap.toLocaleString()} open
        </em>
      </header>
      {joined === 0 ? (
        <p className="stance-roster-empty">
          No one against yet. Be first to bet against{" "}
          <b>{owner.name || "creator"}</b>.
        </p>
      ) : (
        <ul className="stance-roster stance-roster-table">
          <li className="stance-roster-head" aria-hidden>
            <span />
            <span>Player</span>
            <span>How</span>
            <span>Joined</span>
          </li>
          {rosterEntries.map((p) => (
            <li key={p.id}>
              <span className="stance-roster-dot" aria-hidden />
              <b>{p.name}</b>
              <em>{p.role}</em>
              <time className="stance-roster-time" dateTime={p.at || undefined}>
                {formatP2PRosterTime(p.at)}
              </time>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  const renderPicker = () => {
    const creatorStanceTag =
      bet.stance === "against" ? P2P_TERMS.againstTag : "For";
    const counterStanceTag =
      bet.stance === "against" ? "For" : P2P_TERMS.againstTag;
    if (!parts?.options?.length) {
      return (
        <div className="stance-picker stance-picker-text">
          <div className="stance-option stance-option-creator">
            <span className="stance-option-tag">{creatorStanceTag}</span>
            <strong>{parts?.pickLabel || stance}</strong>
          </div>
          <div className="stance-option stance-option-counter">
            <span className="stance-option-tag">{counterStanceTag}</span>
            <strong>
              {bet.stance === "against"
                ? "This outcome happens"
                : P2P_TERMS.anythingButBacking}
            </strong>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`stance-picker stance-picker-${parts.options.length}`}
        role="group"
        aria-label={`Creator ${creatorStanceTag.toLowerCase()}: ${parts.fullText}`}
      >
        {parts.options.map((opt) => {
          const isCreator = opt.id === parts.key;
          return (
            <div
              key={opt.id}
              className={`stance-option ${
                isCreator ? "stance-option-creator" : "stance-option-counter"
              }`}
              title={opt.hint || opt.label}
            >
              <span className="stance-option-tag">
                {isCreator ? creatorStanceTag : counterStanceTag}
              </span>
              <strong>{opt.label}</strong>
              {opt.sublabel && <small>{opt.sublabel}</small>}
              {isCreator && (
                <span className="stance-option-mark" aria-hidden>●</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPredictions = () => {
    if (!preds) return null;
    const total = preds.totalPredictions ?? 0;
    const community = preds.communityPredictions ?? 0;
    const ai = preds.aiPredictions ?? 0;
    return (
      <section className="stance-panel stance-panel-pulse">
        <header>
          <span>Crowd pulse</span>
          <em>{total} predictions</em>
        </header>
        <ul>
          <li>
            <span className="stance-pulse-icon" aria-hidden>
              <Users size={11} />
            </span>
            <span className="stance-pulse-label">Community</span>
            <span className="stance-pulse-val">{community}</span>
          </li>
          <li>
            <span className="stance-pulse-icon" aria-hidden>
              <Bot size={11} />
            </span>
            <span className="stance-pulse-label">AI</span>
            <span className="stance-pulse-val">{ai}</span>
          </li>
          <li>
            <span className="stance-pulse-icon" aria-hidden>
              <Layers size={11} />
            </span>
            <span className="stance-pulse-label">All sources</span>
            <span className="stance-pulse-val">{total}</span>
          </li>
        </ul>
      </section>
    );
  };

  const renderJoinWindow = () => {
    if (!countdown) return null;
    return (
      <section className="stance-panel stance-panel-window">
        <header>
          <span>Join window</span>
          <em>{timeProgress}% elapsed</em>
        </header>
        <div className="stance-window-body">
          <p className="stance-window-countdown">
            <Clock size={10} aria-hidden />
            Locks in {countdown}
          </p>
          <div
            className="stance-fill stance-fill-rail"
            aria-label={`${timeProgress}% of join window elapsed`}
          >
            <span style={{ width: `${timeProgress}%` }} />
          </div>
        </div>
      </section>
    );
  };

  const renderStandings = () => {
    if (!standings?.home && !standings?.away) return null;
    const h = standings.home || {};
    const a = standings.away || {};
    return (
      <section className="stance-panel stance-panel-table">
        <header>
          <span>Table snapshot</span>
          <em>{fixture.league?.name || "League"}</em>
        </header>
        <div className="stance-table">
          <div className="stance-table-row">
            <TeamMark team={home} side="home" />
            <span className="stance-table-name">{home.name || "Home"}</span>
            <span className="stance-table-rank">#{h.rank ?? "—"}</span>
            <span className="stance-table-pts">{h.points ?? 0}<small>pts</small></span>
            <FormStrip form={h.form} label={home.name || "Home"} />
          </div>
          <div className="stance-table-row">
            <TeamMark team={away} side="away" />
            <span className="stance-table-name">{away.name || "Away"}</span>
            <span className="stance-table-rank">#{a.rank ?? "—"}</span>
            <span className="stance-table-pts">{a.points ?? 0}<small>pts</small></span>
            <FormStrip form={a.form} label={away.name || "Away"} />
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <article className={`stance-view stance-view-${tone}`}>
        <header className="stance-context">
          <div className="stance-context-top">
            <span className="stance-context-league">
              {fixture.league?.logo && (
                <img src={fixture.league.logo} alt="" />
              )}
              {fixture.league?.name || "League"}
            </span>
            <div className="stance-context-badges">
              {liveScore && (
                <span className="stance-score-chip" title={liveScore.label}>
                  <strong>{liveScore.score}</strong>
                  <em>{liveScore.label}</em>
                </span>
              )}
              {live && !liveScore && (
                <span className="stance-pill stance-pill-live">
                  <CircleDot size={7} /> Live
                </span>
              )}
              <span className={`stance-pill stance-pill-${tone}`}>
                {bet.status}
              </span>
              {outcomeStatus && outcomeStatus !== "Pending" && (
                <span className={`stance-pill stance-pill-outcome stance-pill-outcome-${outcomeStatus.toLowerCase()}`}>
                  <Trophy size={8} aria-hidden /> {outcomeStatus}
                </span>
              )}
              <ShareIconButton
                className="stance-share-btn"
                size={12}
                url={buildP2PShareLink(bet._id)}
                text={buildP2PShareText(bet)}
                title="BetBond P2P"
                ariaLabel="Share this P2P"
                onShared={(mode) =>
                  notify(mode === "copied" ? "Link copied." : "Shared.")
                }
                onError={(message) => notify(message, "error")}
              />
            </div>
          </div>

          <div className="stance-context-fixture" aria-label="Fixture">
            <div className="stance-context-side stance-context-side-home">
              <TeamMark team={home} side="home" />
              <strong className="stance-context-team">{home.name || "Home"}</strong>
            </div>
            <span className="stance-context-vs" aria-hidden>
              v
            </span>
            <div className="stance-context-side stance-context-side-away">
              <TeamMark team={away} side="away" />
              <strong className="stance-context-team">{away.name || "Away"}</strong>
            </div>
          </div>

          {kickoff ? (
            <p className="stance-context-kickoff">{kickoff}</p>
          ) : null}
        </header>

        <div
          className="stance-grid"
          style={
            isWideStance ? { "--pit-stance-rail-w": `${railWidth}px` } : undefined
          }
        >
          <div className="stance-main">
            <section className="stance-author">
              <div className="stance-avatar" aria-hidden>
                {initials(owner.name)}
              </div>
              <div className="stance-author-meta">
                <p className="stance-author-name">
                  {owner.name || "Creator"}
                  <span>created a P2P</span>
                </p>
                <p className="stance-author-sub">
                  {posted && <span>{posted}</span>}
                  {countdown && (
                    <span className="stance-author-lock">
                      <Clock size={9} aria-hidden /> locks in {countdown}
                    </span>
                  )}
                </p>
              </div>
            </section>

            <section className="stance-block stance-block-take">
              <p className="stance-eyebrow">The take</p>
              {parts?.badge && (
                <span className="stance-market">{parts.badge}</span>
              )}
              {!parts?.options?.length && (
                <blockquote>
                  <Quote size={11} aria-hidden />
                  <span>{stance}</span>
                </blockquote>
              )}
              {renderPicker()}
            </section>

            <section className="stance-block stance-block-pact">
              <p className="stance-eyebrow">The pact</p>
              <dl className="stance-pact stance-pact-5">
                <div>
                  <dt>Offered odds</dt>
                  <dd>@{getOfferedOdds(bet)}</dd>
                </div>
                <div>
                  <dt>Min stake</dt>
                  <dd>{getMinCounterStake(bet)}<small>cash</small></dd>
                </div>
                <div className="stance-pact-win">
                  <dt>Win pot</dt>
                  <dd>{winPayout}<small>cash</small></dd>
                </div>
                <div>
                  <dt>Open</dt>
                  <dd>
                    {remainingCap.toLocaleString()}
                    <small> cash</small>
                  </dd>
                </div>
                <div>
                  <dt>Creator budget</dt>
                  <dd>{getCreatorBudget(bet)}<small>cash</small></dd>
                </div>
              </dl>
            </section>

            <section
              className={`stance-block stance-block-counter stance-block-move stance-block-move--${action.tone}`}
            >
              <header className="stance-move-head">
                <p className="stance-eyebrow">
                  <CornerDownRight size={11} aria-hidden /> Your move
                </p>
                {moveBadge && (
                  <span className={`stance-move-badge stance-move-badge--${action.tone}`}>
                    {moveBadge}
                  </span>
                )}
              </header>
              <div className="stance-move-body">{renderMoveBody()}</div>
              <div className="stance-counter-actions">{renderActions()}</div>
            </section>

            {renderRoster()}
          </div>

          <div className="stance-rail-wrap">
            {isWideStance && (
              <div
                className="pit-resize-handle pit-resize-handle-rail"
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={railWidth}
                aria-valuemin={railMin}
                aria-valuemax={railMax}
                aria-label="Resize insight panel"
                title="Drag to resize · double-click to reset"
                onPointerDown={onRailResizePointerDown}
                onDoubleClick={resetRailWidth}
              />
            )}
            <aside className="stance-rail">
            {siblingStances.length > 0 && (
              <section className="stance-panel stance-panel-siblings">
                <header>
                  <span>More on this match</span>
                  <em>{siblingStances.length}</em>
                </header>
                <ul className="stance-siblings">
                  {siblingStances.map((s) => {
                    const sOwner = getOwner(s);
                    const sPick = formatOutcome(s.agreedOdds, s.fixture || {});
                    return (
                      <li key={s._id}>
                        <button
                          type="button"
                          className="stance-sibling-btn"
                          onClick={() => onSelectStance?.(s._id)}
                        >
                          <span className="stance-sibling-who">
                            {sOwner.name || "Creator"}
                          </span>
                          <span className="stance-sibling-pick" title={sPick}>
                            {sPick}
                          </span>
                          <span className="stance-sibling-meta">
                            Cash {getCreatorBudget(s)} · @{getOfferedOdds(s)} ·{' '}
                            {getJoinedCount(s)} counter
                            {getJoinedCount(s) === 1 ? '' : 's'}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {renderPredictions()}
            {renderStandings()}
            {renderJoinWindow()}

            <div className="stance-rail-primer">
              <P2PHowItWorks />
            </div>

            {pendingInvites.length > 0 && (
              <section className="stance-panel stance-panel-invites">
                <header>
                  <span>Waiting on invites</span>
                  <em>{pendingInvites.length}</em>
                </header>
                <ul className="stance-invites">
                  {pendingInvites.map((inv, i) => {
                    const name =
                      typeof inv.user === "object"
                        ? inv.user?.name
                        : null;
                    return (
                      <li key={i}>
                        <span className="stance-invite-dot" aria-hidden />
                        {name || "Invited player"}
                        <em>pending</em>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

          </aside>
          </div>
        </div>
      </article>

      <JoinP2PModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onConfirm={handleJoinConfirm}
        bet={bet}
        loading={busy}
        mode={joinMode}
        userId={userId}
      />
      <CancelP2PModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancelConfirm}
        loading={busy}
      />
      <EditP2PModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditConfirm}
        bet={bet}
        loading={busy}
      />
    </>
  );
};

export default PitDuelSlip;
